import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import { Fields, reduxForm, reset } from 'redux-form';
import { Button, Modal } from 'react-bootstrap';
import { connect } from 'react-redux';
import {
  TextInput,
  TextArea,
  FormErrorMessages,
  scrollToFirstError,
  generateValidation,
  SharedFormMessages as formMessages,
} from 'ecs-react-components/dist/index.es';

import validations from './SendSecurelyForm.validations';
import { messages } from './SendSecurelyForm.i18n';
import { sendHealthSummary } from '../../redux/modules/health-summary/health-summary-middleware';

export const fields = ['recipient', 'emailBody'];

class SendSecurelyForm extends Component {
  constructor(props) {
    super(props);
    this.title = null;
  }

  onSubmit = (formValues) => {
    const {
      dispatch,
      healthSummary,
      patient,
      languageCode,
    } = this.props;

    const postBody = {
      patientId: patient.patientId,
      ifSystemId: healthSummary.currentIdentifier.ifSystemId,
      recipientAddress: formValues.recipient,
      messageText: formValues.emailBody,
      transmitType: 'sendSecurely',
      formName: 'sendCcdaSecurelyForm',
    };
    const identifier = healthSummary.currentIdentifier.ccdaId ?
      {
        // MOSAIQ
        identifier: {
          ccdaId: healthSummary.currentIdentifier.ccdaId,
        },
      } :
      {
        // ARIA
        identifier: {
          startDate: healthSummary.currentIdentifier.startDate,
          endDate: healthSummary.currentIdentifier.endDate,
        },
      };
    dispatch(sendHealthSummary({ ...postBody, ...identifier }, languageCode));
    this.props.close();
  }

  close = () => {
    this.props.dispatch(reset('sendCcdaSecurelyForm'));
    this.props.close();
  }

  renderFields = (fieldsMeta) => {
    const { formatMessage } = this.props.intl;
    const getLabel = (f) => f + 'Label';
    const getHint = (f) => f + 'Hint';

    const { recipient, emailBody } = fieldsMeta;

    return (
      <div>
        <div className="form-group">
          <label id={getLabel(recipient.input.name)}
            className="control-label form-label"
            htmlFor={recipient.input.name}
            data-qa-item="RecipientLabel"
          >
            {formatMessage(messages.recipient.label)}&nbsp;
            <FormErrorMessages field={recipient} emailValidation={messages.recipient.validation}
              ariaDescribedby={getLabel(recipient.input.name)}
              customMessage={formatMessage(formMessages.error.email)}
            />
          </label>
          <TextInput
            field={recipient}
            placeholder={formatMessage(messages.recipient.placeholder)}
            email={validations.recipient.email}
            qaTag="Recipient"
            ariaLabelledBy={getLabel(recipient.input.name)}
          />
          <span id={getHint(recipient.input.name)} className="sr-only">
            {formatMessage(messages.recipient.placeholder)}
          </span>
        </div>
        <div className="form-group">
          <label id={getLabel(emailBody.input.name)}
            className="control-label form-label"
            htmlFor={emailBody.input.name}
            data-qa-item="MessageBodyLabel"
          >
            {formatMessage(messages.body.label)}&nbsp;
            <FormErrorMessages
              field={emailBody}
              ariaDescribedby={getLabel(emailBody.input.name)}
            />
          </label>
          <TextArea
            field={emailBody}
            placeholder={formatMessage(messages.body.placeholder)}
            qaTag="MessageBody"
            ariaLabelledBy={getLabel(emailBody.input.name)}
            rows={5}
            maxLength={validations.emailBody.maxLength}
          />
          <span id={getHint(emailBody.input.name)} className="sr-only">
            {formatMessage(messages.body.placeholder)}
          </span>
        </div>
      </div>
    );
  }

  render() {
    const { formatMessage } = this.props.intl;
    const { handleSubmit } = this.props;

    return (
      <Modal
        show={ this.props.showModal }
        onHide={ this.close }
        backdrop="static"
        aria-labelledby="modalTitle"
      >
        <Modal.Header closeButton>
          <Modal.Title aria-describedby="modalTitle" data-qa-item="modal-title">
            <div tabIndex="0" id="modalTitle">
              {formatMessage(messages.page.header)}
            </div>
          </Modal.Title>
        </Modal.Header>
        <form onSubmit={handleSubmit(this.onSubmit)}>
          <Modal.Body>
            <p data-qa-item="PrimaryExplanationTop" aria-describedby="primaryTop" tabIndex="0">
              <span id="primaryTop">{formatMessage(messages.explanation.primary.top)}</span>
            </p>
            <p data-qa-item="PrimaryExplanationBottom" aria-describedby="primaryBottom" tabIndex="0">
              <span id="primaryBottom">
                <FormattedMessage
                  { ...messages.explanation.primary.bottom }
                  values={{
                    transmitType: <em>{ formatMessage(messages.explanation.transmitType) }</em>,
                  }}
                />
              </span>
            </p>
            <Fields names={fields} component={this.renderFields} />
            <p data-qa-item="SecondaryExplanation" aria-describedby="secondary" tabIndex="0">
              <span id="secondary">{formatMessage(messages.explanation.secondary)}</span>
            </p>
          </Modal.Body>
          <Modal.Footer>
            <Button type="submit"
              className="btn btn-primary"
              data-qa-item="SubmitButton"
              onClick={this.handleSubmitClick}
            >
              <FormattedMessage {...messages.send.label} />
            </Button>
            <Button type="button" className="btn btn-default" onClick={ this.close } data-qa-item="CancelButton">
              <FormattedMessage {...messages.cancel.label} />
            </Button>
          </Modal.Footer>
        </form>
      </Modal>
    );
  }
}

SendSecurelyForm.propTypes = {
  intl: PropTypes.object,
  patient: PropTypes.object,
  dispatch: PropTypes.func,
  handleSubmit: PropTypes.func,
  showModal: PropTypes.bool,
  close: PropTypes.func,
  healthSummary: PropTypes.object,
  externalSystems: PropTypes.array,
  languageCode: PropTypes.string,
};

const formConfig = {
  form: 'sendCcdaSecurelyForm',
  enableReinitialize: true,
  fields,
  onSubmitFail: (errors) => scrollToFirstError(errors, fields),
  ...generateValidation(validations),
};

const mapStateToProps = (state) => ({
  patient: state.profile.patient,
  healthSummary: state.healthSummary,
  externalSystems: state.externalSystems.externalSystems,
  languageCode: state.language.languageCode,
});

export default reduxForm(formConfig)(connect(mapStateToProps)(injectIntl(SendSecurelyForm)));
